import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import useDimension from "@/hooks/use-dimension";
import HomeFilter from "@/components/home-filter";
import { api } from "@/utils/api";
import { WorkerProfileCard } from "@/components/workerProfileCard";
import { useSelector } from "react-redux";
import { FilterI } from "@/types/interfaces";
import { useTranslation } from 'react-i18next';
import { WorkerProfileCardSkeleton } from "@/utils/WorkerProfileCardSkeleton";
import { normalizeWorkersListResponse, type WorkersListPayload } from "@/vike/ssrHelpers";
import {
  uiStateFromFilterPayload,
  rememberHomeSearch,
  readHomeSearchForMount,
  type HomeFilterUiState,
} from "@/utils/homeSearchParams";
import { isAuthenticated } from "@/shared/services/credentialsService";
import { useNavigate } from "react-router-dom";
import {
  clearRequestQuoteDraft,
  createEmptyRequestQuoteDraft,
  writeRequestQuoteDraft,
} from "@/utils/requestQuoteDraft";
import HowItWorks from "@/components/home-16/HowItWorks";

const words = ["electrician", "caregiver", "groomer", "handyman", "tutor"];

const DEFAULT_PAGE_SIZE = 12;

/** Default homepage listing (SSR + client fallback after SPA navigation e.g. logout). */
const buildDefaultHomePayload = (limit: number) => ({
  isShow: true,
  page: 1,
  limit,
});

/** Strip UI-only fields before calling the workers filter API. */
function toApiFilterPayload(payload: Record<string, any>) {
  const { municipalityValues, ...apiFields } = payload || {};
  return apiFields;
}

type Hero16Props = {
  initialWorkers?: unknown;
};

const Index = ({ initialWorkers }: Hero16Props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [currentWord, setCurrentWord] = useState(0);
  const { isMobile, width } = useDimension();
  // Match font-size so the rotating word shares the same baseline as surrounding text
  const animatedWordHeight = "1em";
  /** Skip enter animation on first paint so the word is never opacity/height 0 in WebViews */
  const [wordCycleReady, setWordCycleReady] = useState(false);
  useEffect(() => {
    setWordCycleReady(true);
  }, []);
  const currentWordLabel = String(t(words[currentWord])).toLowerCase();
  // Size the slot to the longest rotating word so layout stays stable and inline
  const longestWordLabel = useMemo(
    () =>
      words
        .map((word) => String(t(word)).toLowerCase())
        .reduce((longest, word) =>
          word.length > longest.length ? word : longest
        ),
    // Recompute when language changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language]
  );
  const cachedRef = useRef(readHomeSearchForMount());
  const cached = cachedRef.current;

  const [workers, setWorkers] = useState<WorkersListPayload>(() =>
    cached?.workers
      ? normalizeWorkersListResponse(cached.workers)
      : normalizeWorkersListResponse(initialWorkers)
  );
  const [isLoadingWorkers, setIsLoadingWorkers] = useState(false);
  const { user } = useSelector((state: { user: any }) => state.user); // Get logged-in user
  const [offset, setOffset] = useState<number>(cached?.page || 1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [currentFilters, setCurrentFilters] = useState<any>(
    cached?.apiPayload || {}
  );
  const [filterUiState, setFilterUiState] = useState<HomeFilterUiState | null>(
    cached?.ui || null
  );
  const defaultListFetchStarted = useRef(false);

  const [filterValue, setFilterValue] = useState<FilterI>({
    text: '',
    page: offset,
    limit: pageSize,
    order: 'desc',
    clientName: '',
    workerName: '',
    status: ''
  });

  const persistSnapshot = useCallback(
    (
      apiFields: Record<string, unknown>,
      ui: HomeFilterUiState,
      page: number,
      nextWorkers?: WorkersListPayload
    ) => {
      rememberHomeSearch({
        apiPayload: apiFields,
        ui,
        page,
        workers: nextWorkers,
      });
    },
    []
  );

  const applySearch = useCallback(
    async (
      payload: any,
      meta?: { ui?: HomeFilterUiState; page?: number }
    ) => {
      try {
        setIsLoadingWorkers(true);
        const apiFields = toApiFilterPayload(payload);
        const result = await api.getFilterWorkersList(apiFields);
        const normalized = normalizeWorkersListResponse(result ?? null);
        setWorkers(normalized);

        const page = meta?.page ?? payload?.page ?? 1;
        const ui = meta?.ui || uiStateFromFilterPayload(apiFields);
        if (Object.keys(apiFields).length > 0 && !apiFields.isShow) {
          persistSnapshot(apiFields, ui, page, normalized);
        }
      } catch (error) {
        console.error('Error fetching filtered data:', error);
        setWorkers(normalizeWorkersListResponse(null));
      } finally {
        setIsLoadingWorkers(false);
      }
    },
    [persistSnapshot]
  );

  const handleChangePage = (event: unknown, newPage: number) => {
    setOffset(newPage);
    const combinedFilters = {
      ...currentFilters,
      page: newPage,
      limit: pageSize
    };
    const ui = filterUiState || uiStateFromFilterPayload(currentFilters);
    void applySearch(combinedFilters, { ui, page: newPage });
  };


  // Cycle through words
  useEffect(() => {
    const timeout = setTimeout(() => {
      setCurrentWord((prev) => (prev + 1) % words.length);
    }, 5000);
    return () => clearTimeout(timeout);
  }, [currentWord]);

  const goToRequestQuoteCompose = useCallback(
    (apiFields: Record<string, unknown>, ui: HomeFilterUiState) => {
      clearRequestQuoteDraft();
      const draft = createEmptyRequestQuoteDraft({
        apiPayload: apiFields,
        ui,
      });
      writeRequestQuoteDraft(draft);
      navigate("/request-quote", {
        state: { apiPayload: apiFields, ui },
      });
    },
    [navigate]
  );

  // Callback to filter workers — logged-in clients go to compose with filters
  const filterPayload = useCallback(
    (payload: any) => {
      const { municipalityValues, ...apiFields } = payload || {};
      const ui = uiStateFromFilterPayload(apiFields, municipalityValues);
      setCurrentFilters(apiFields);
      setFilterUiState(ui);
      setOffset(1);
      // Save filters immediately so Back works even before the request finishes
      persistSnapshot(apiFields, ui, 1);

      if (isAuthenticated() && user?.type === "user") {
        goToRequestQuoteCompose(apiFields, ui);
        return;
      }

      void applySearch(
        {
          ...apiFields,
          page: 1,
          limit: pageSize,
        },
        { ui, page: 1 }
      );
    },
    [pageSize, applySearch, persistSnapshot, goToRequestQuoteCompose, user?.type]
  );

  // If we restored filters from sessionStorage without a cached listing, refetch once
  useEffect(() => {
    const c = cachedRef.current;
    if (!c?.apiPayload || Object.keys(c.apiPayload).length === 0) return;
    if (c.workers) {
      defaultListFetchStarted.current = true;
      return;
    }
    defaultListFetchStarted.current = true;
    void applySearch(
      {
        ...c.apiPayload,
        page: c.page || 1,
        limit: pageSize,
      },
      { ui: c.ui, page: c.page || 1 }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Client fallback: after SPA nav (e.g. logout → /), onBeforeRender does not re-run.
  // Skip when restoring a cached search from Back.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (defaultListFetchStarted.current) return;
    if (Object.keys(currentFilters).length > 0) return;
    if ((workers.data?.length ?? 0) > 0) return;

    defaultListFetchStarted.current = true;
    setOffset(1);
    void applySearch(buildDefaultHomePayload(pageSize));
  }, [applySearch, currentFilters, pageSize, workers.data?.length]);

 

  // Filter out the logged-in worker
  const filteredWorkers = useMemo(
    () => workers?.data?.filter((candidate) => candidate?.user?._id !== user?._id),
    [workers, user]
  );

  // Memoized content — browse-only on homepage (quote selection lives on /request-quote)
  const content = useMemo(
    () =>
      filteredWorkers?.map((candidate) => (
        <WorkerProfileCard
          key={candidate.id}
          candidate={candidate}
          selectionMode="auto"
        />
      )),
    [filteredWorkers]
  );
  const hasUserSearched = useMemo(
    () => Object.keys(currentFilters || {}).length > 0,
    [currentFilters]
  );

  return (
    <div className=" bg-[#f5f5f3] flex flex-col gap-2 w-full pt-0 jandi-guidance-page">
      <motion.section className="banner-section-four -type-16 relative min-h-[60vh] sm:min-h-[80vh] overflow-x-clip overflow-y-visible">
        <motion.div className="small-container flex flex-col justify-center items-start md:min-w-max min-h-[60vh] sm:min-h-[80vh] px-4 sm:px-12 mt-5 overflow-visible">
          <motion.div className="content-box overflow-visible">
            {/* No AOS here: Instagram in-app browser often never applies aos-animate, leaving opacity:0 */}
            <div className="title-box">
              <h3
                className="text-3xl sm:text-5xl"
                style={{
                  maxWidth: "920px",
                  textAlign: "left",
                  overflow: "visible",
                  fontFamily: '"Object Sans", system-ui, sans-serif',
                  fontWeight: 700,
                  lineHeight: isMobile || width === 0 ? 1.25 : 1.15,
                }}
              >
                {t('hireElectricianTrusted')}
                <span
                  style={{
                    display: "inline-block",
                    position: "relative",
                    height: animatedWordHeight,
                    lineHeight: 1,
                    overflow: "hidden",
                    verticalAlign: "baseline",
                    marginLeft: "0.25em",
                  }}
                >
                  {/* Invisible sizer keeps the slot width inline with surrounding text */}
                  <span
                    aria-hidden
                    style={{
                      visibility: "hidden",
                      whiteSpace: "nowrap",
                      display: "inline-block",
                      lineHeight: 1,
                    }}
                  >
                    {longestWordLabel}
                  </span>
                  <AnimatePresence initial={false} mode="popLayout">
                    <motion.span
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        whiteSpace: "nowrap",
                        lineHeight: 1,
                        color: "var(--primary-color)",
                      }}
                      key={currentWord}
                      variants={{
                        enter: {
                          y: "100%",
                          opacity: 0,
                        },
                        center: {
                          y: "0%",
                          opacity: 1,
                        },
                        exit: {
                          y: "-100%",
                          opacity: 0,
                        },
                      }}
                      initial={wordCycleReady ? "enter" : false}
                      animate="center"
                      exit="exit"
                      transition={{
                        y: { type: "spring", duration: 1 },
                        opacity: { duration: 0.35 },
                      }}
                    >
                      {currentWordLabel}
                    </motion.span>
                  </AnimatePresence>
                </span>{" "}
                <br />
                {t('trustedTask')}
              </h3>
            </div>

            <HomeFilter
              filterPayload={filterPayload}
              isSearching={isLoadingWorkers}
              initialFilterState={filterUiState}
            />
          </motion.div>
        </motion.div>
        
        {/* JandiInfoLeft positioned at bottom-left of banner */}
        {/* <div className="absolute sm:bottom-10 bottom-2 sm:left-14 left-5  text-justify" >
          <JandiInfoLeft />
        </div> */}
      </motion.section>
      {/* <Spinner isLoading={isLoadingWorkers} size='medium' classname="mb-6" /> */}
      {isLoadingWorkers ? (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 xl:gap-10 px-4 xl:px-6 2xl:px-8 py-10 mx-auto max-w-[1800px]">
    {[...Array(8)].map((_, i) => (
      <WorkerProfileCardSkeleton key={i} />
    ))}
  </div>
) :
!isLoadingWorkers && (filteredWorkers?.length > 0 || workers?.isOtherAreasFallback) && (
  <div className="flex flex-col gap-8 px-4 xl:px-6 2xl:px-8 justify-center pb-10 mx-auto max-w-[1800px]">
    {workers?.isOtherAreasFallback && workers?.emptyAreaMessage && (
      <div
        role="status"
        className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 text-center"
      >
        {workers.emptyAreaMessage}
        {filteredWorkers?.length > 0
          ? ` ${t('otherRegionsProvidersAvailable')}`
          : ''}
      </div>
    )}

    {filteredWorkers?.length > 0 && (
      <>
    <div>
      <h1 className="text-4xl font-bold mb-8 text-center">
        {t('bestProfessionalsNearYou')}{' '}
        <span className="text-darkBlue">{t('professionals')}</span>{' '}
        {t('nearYou')}
      </h1>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 xl:gap-10">
      {content}
    </div>

    {/* Simple Pagination: < 1 of 300 > */}
    <div className="flex items-center justify-center gap-4">
      <button
        onClick={() => handleChangePage(null, offset - 1)}
        disabled={offset === 1}
        className="flex items-center justify-center w-8 h-8 border border-black rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      
      <span className="text-sm text-gray-700 min-w-[80px] text-center">
        {offset} of {workers?.totalPages || 1}
      </span>
      
      <button
        onClick={() => handleChangePage(null, offset + 1)}
        disabled={offset === workers?.totalPages}
        className="flex items-center justify-center w-8 h-8 border border-black rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="https://www.w3.org/2000/svg">
          <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </div>
      </>
    )}
  </div>
)}

    {!isLoadingWorkers && hasUserSearched && filteredWorkers?.length === 0 && !workers?.isOtherAreasFallback && (
      <p className="text-base font-semibold text-center">
        {t('noDataAvailableAgainstFilters')}
      </p>
    )}

      <HowItWorks />

    </div>
  );
};

export default Index;

